import { Button, Stack, Theme, Typography } from '@mui/material'
import algo from '../../core/Algo'
import { QueryStatus } from '../hooks/useQuery'
import { ChartDataWithUnit } from './ChartsHelper'
import HorizontalBarChart from './HorizontalBarChart'

interface P<T> {
    data: ChartDataWithUnit<'bar', T, string>
    status: QueryStatus
    theme: Theme
    refetch?: () => void
}

export type ChartValidData = number[] | number[][] | null

export const HorizontalBarChartResult = <T extends ChartValidData>({ data, status, theme, refetch }: P<T>) => {
    const { number } = algo
    switch (status) {
        case 'success':
            return (
                <HorizontalBarChart
                    data={data}
                    options={{
                        plugins: {
                            datalabels: {
                                color: context => {
                                    const yAxisData = context.dataset.data as number[]
                                    const currentValue = yAxisData[context.dataIndex]
                                    if (currentValue === 0) return 'transparent'
                                    const max = Math.max(...yAxisData)
                                    return (currentValue / max) < 0.5 ? theme.palette.text.secondary : theme.palette.secondary.contrastText
                                },
                                anchor: 'end',
                                align: context => {
                                    const yAxisData = context.dataset.data as number[]
                                    const currentValue = yAxisData[context.dataIndex]
                                    const max = Math.max(...yAxisData)
                                    return (currentValue / max) < 0.5 ? 'end' : 'start'
                                },
                                offset: 10,
                                formatter: value => '$ ' + number(value)
                            },
                            legend: {
                                display: false
                            },
                            annotation: {
                                annotations: data?.datasets?.[0]?.data?.some(e => e > 0)
                                    ? {}
                                    : {
                                            label1: {
                                                type: 'label',
                                                content: ['No data available for the selected time period'],
                                                position: 'center',
                                                color: theme.palette.text.secondary
                                            }
                                        }
                            }
                        },
                        scales: {
                            x: {
                                display: false,
                                grid: {
                                    display: false
                                }
                            },
                            y: {
                                beginAtZero: false,
                                grid: {
                                    display: false
                                }
                            }
                        }
                    }}
                />
            )

        case 'loading':
            return (
                <HorizontalBarChart
                    isLoading={true}
                    data={{
                        datasets: [],
                        labels: []
                    }}
                />
            )
        case 'error':
            return (
                <Stack spacing={2} sx={{ justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                    <Typography>
                        There was an error during the data fetching
                    </Typography>
                    <Button variant='contained' color='error' onClick={() => refetch?.()}>
                        Retry
                    </Button>
                </Stack>
            )
    }
}
